import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AlertOctagon, AlertTriangle, Box, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { Input, Select } from "../UI/FormField";
import { Modal } from "../UI/Modal";
import { AssignmentSelect } from "../directory/AssignmentSelect";
import { VehicleForm } from "../vehicles/VehicleForm";
import { useAppDispatch } from "../../hooks/useStore";
import { createVehicle } from "../../store/slices/vehiclesSlice";
import type { CreateVehicle, Vehicle } from "../../types/vehicles";
import IntakeField from "./IntakeField";
import IntakeNote from "./IntakeNote";
import {
  CONTAINER_CARGO_TYPES,
  CONTAINER_TYPE_OPTIONS,
} from "../../constants/containerOrderConstants";
import { IMO_CLASS_OPTIONS } from "../../constants/formOptions";
import type {
  IntakeCargoItem,
  IntakeIssue,
  IntakeLine,
} from "../../types/containerOrderIntake";
import { issuesAt } from "../../utils/intakeDraft";
import { vehicleLabel } from "../../utils/vehicleLabel";

interface IntakeContainerCardProps {
  index: number;
  line: IntakeLine;
  /** The order's type, which a blank container type inherits. */
  orderType: string;
  /** What Oliver wrote for this container, for the "was" hints. */
  source?: Record<string, any>;
  truckLabel: string;
  issues: IntakeIssue[];
  readOnly?: boolean;
  onChange: (path: string, value: unknown) => void;
  onTruck: (index: number, id: string | null, label: string) => void;
}

const numberOrNull = (value: string) => {
  if (value.trim() === "") return null;
  const n = Number(value.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const EMPTY_ROW: IntakeCargoItem = {
  quantity: null,
  type: "",
  weightKg: null,
  description: "",
};

/**
 * One Oliver container, as the container it will become.
 *
 * Everything the office would otherwise type on the container page after
 * generating it — number, seal, truck, handling, packing list — plus the three
 * notes the driver reads at the pickup terminal, the loading site and the
 * drop-off terminal.
 */
const IntakeContainerCard: React.FC<IntakeContainerCardProps> = ({
  index,
  line,
  orderType,
  source,
  truckLabel,
  issues,
  readOnly,
  onChange,
  onTruck,
}) => {
  const { t } = useTranslation(["containerOrderIntake", "containerOrders"]);
  const dispatch = useAppDispatch();
  const [addingTruck, setAddingTruck] = useState(false);
  const [savingTruck, setSavingTruck] = useState(false);
  const base = `lines.${index}`;

  /**
   * How the document names this container's truck: the number on the line and
   * the order's OPT reference. Both are shown, because either can be what the
   * fleet was searched by — and when neither found a truck, what is written is
   * what the office needs in order to pick one.
   */
  const truckNames = [line.truckNr, line.opt].filter(Boolean);

  /**
   * Add the truck the document names to Vehicles, then put it on this
   * container. Saved straight away — the same record the Vehicles page would
   * have created — so Accept never has to.
   */
  const handleCreateTruck = async (data: CreateVehicle | Partial<Vehicle>) => {
    setSavingTruck(true);
    try {
      const created: Vehicle = await dispatch(createVehicle(data as CreateVehicle)).unwrap();
      if (created.type === "TRUCK") {
        onTruck(index, created._id, vehicleLabel(created));
      }
      toast.success(t("containers.truckAdded", { plate: vehicleLabel(created) }));
      setAddingTruck(false);
    } catch (err: unknown) {
      toast.error(typeof err === "string" ? err : t("containers.truckAddFailed"));
    } finally {
      setSavingTruck(false);
    }
  };
  const own = issuesAt(issues, base);
  const set = (field: string, value: unknown) => onChange(`${base}.${field}`, value);

  const textField = (field: string, label: string, upper = true) => (
    <IntakeField path={`${base}.${field}`} label={label} issues={issues}>
      {(error) => (
        <Input
          size="sm"
          value={(line as any)[field] ?? ""}
          error={error}
          disabled={readOnly}
          onChange={(e) => set(field, upper ? e.target.value.toUpperCase() : e.target.value)}
        />
      )}
    </IntakeField>
  );

  const numberField = (field: string, label: string) => (
    <IntakeField path={`${base}.${field}`} label={label} issues={issues}>
      {(error) => (
        <Input
          size="sm"
          inputMode="decimal"
          value={(line as any)[field] ?? ""}
          error={error}
          disabled={readOnly}
          onChange={(e) => set(field, numberOrNull(e.target.value))}
        />
      )}
    </IntakeField>
  );

  const setRow = (row: number, patch: Partial<IntakeCargoItem>) =>
    set(
      "cargoItems",
      line.cargoItems.map((item, i) =>
        // Touching a row's type is the reviewer's answer to the guess.
        i === row ? { ...item, ...patch, ...(patch.type !== undefined ? { typeGuessed: false } : {}) } : item,
      ),
    );

  const border = own.some((i) => i.severity === "blocking")
    ? "border-red-300"
    : own.length
      ? "border-amber-300"
      : "border-[#E5E3DC]";

  return (
    <div data-intake-path={base} className={`bg-white border ${border} rounded-lg p-3 space-y-3`}>
      <div className="flex items-center gap-2 text-[14px] font-medium text-[#0B0F0E]">
        <Box className="h-4 w-4 text-[#C97A3C]" />
        {t("containers.title", { n: index + 1 })}
        {line.containerNumber && (
          <span className="font-mono text-[12px] text-[#4A5550]">{line.containerNumber}</span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {textField("containerNumber", t("containers.number"))}
        <IntakeField
          path={`${base}.containerType`}
          label={t("containers.type")}
          issues={issues}
          source={!line.containerType ? source?.containerType || null : null}
          hint={!line.containerType && orderType ? t("containers.inherits", { type: orderType }) : undefined}
        >
          {(error) => (
            <Select
              size="sm"
              value={line.containerType ?? ""}
              error={error}
              disabled={readOnly}
              placeholder={t("containers.sameAsOrder")}
              options={CONTAINER_TYPE_OPTIONS}
              onChange={(e) => set("containerType", e.target.value)}
            />
          )}
        </IntakeField>
        {textField("sealNumber", t("containers.seal"))}
        <IntakeField
          path={`${base}.vehicleId`}
          label={t("containers.truck")}
          issues={issues}
          source={!line.vehicleId ? truckNames.join(" / ") || null : null}
        >
          {(error) => (
            <div className="flex gap-2">
              <div className="flex-1 min-w-0">
                <AssignmentSelect
                  source="trucks"
                  size="sm"
                  selectedId={line.vehicleId}
                  value={truckLabel}
                  error={error}
                  disabled={readOnly}
                  onSelect={(pick) => onTruck(index, pick.id, pick.label)}
                  onClear={() => onTruck(index, null, "")}
                />
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setAddingTruck(true)}
                  className="shrink-0 h-8 rounded-md border border-[rgba(11,15,14,0.15)] bg-white px-3 text-[12px] font-medium shadow-sm hover:bg-[#F5F4F0]"
                >
                  {t("containers.addTruck")}
                </button>
              )}
            </div>
          )}
        </IntakeField>
        {textField("mrnNumber", t("containers.mrn"))}
        {textField("isoCode", t("containers.isoCode"))}
        {numberField("tareKg", t("containers.tare"))}
        {numberField("maxPayloadKg", t("containers.maxPayload"))}
      </div>

      {/* Handling */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!line.handling?.temperatureSensitive}
              disabled={readOnly}
              onChange={(e) => set("handling.temperatureSensitive", e.target.checked)}
            />
            {t("containers.temperatureSensitive")}
          </label>
          {line.handling?.temperatureSensitive && (
            <Input
              size="sm"
              value={line.handling.temperatureRange ?? ""}
              disabled={readOnly}
              placeholder={t("containers.temperatureRange")}
              onChange={(e) => set("handling.temperatureRange", e.target.value)}
            />
          )}
        </div>
        <div className="space-y-2" data-intake-path={`${base}.handling.imoClass`}>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!line.handling?.adr}
              disabled={readOnly}
              onChange={(e) => set("handling.adr", e.target.checked)}
            />
            {t("containers.adr")}
          </label>
          {line.handling?.adr && (
            <Select
              size="sm"
              value={line.handling.imoClass ?? ""}
              disabled={readOnly}
              placeholder={t("containers.imoClass")}
              options={IMO_CLASS_OPTIONS}
              onChange={(e) => set("handling.imoClass", e.target.value)}
            />
          )}
        </div>
      </div>

      {/* Packing list */}
      <div data-intake-path={`${base}.cargoItems`} className="space-y-1.5">
        <div className="text-[12px] font-medium text-[#4A5550]">{t("containers.cargo")}</div>
        {line.cargoItems.length === 0 && (
          <div className="text-[12px] text-[#8A9590]">{t("containers.noCargo")}</div>
        )}
        {line.cargoItems.map((item, row) => {
          const rowIssues = issuesAt(issues, `${base}.cargoItems.${row}`);
          const rowBlocking = rowIssues.some((i) => i.severity === "blocking");
          // A cargo row is a bare grid, not an IntakeField, so its issues have
          // no label to sit under — they are listed beneath the row instead.
          const rowNotes = rowIssues.filter((i) => i.message);
          return (
            <div key={row} data-intake-path={`${base}.cargoItems.${row}`}>
              <div
                className={`grid grid-cols-[70px_110px_100px_1fr_auto] gap-2 items-center ${
                  rowBlocking ? "bg-red-50 rounded" : ""
                }`}
              >
                <Input
                  size="sm"
                  inputMode="numeric"
                  value={item.quantity ?? ""}
                  disabled={readOnly}
                  error={rowBlocking && item.quantity == null}
                  placeholder={t("containers.qty")}
                  onChange={(e) => setRow(row, { quantity: numberOrNull(e.target.value) })}
                />
                <div data-intake-path={`${base}.cargoItems.${row}.type`}>
                  <Select
                    size="sm"
                    value={item.type ?? ""}
                    disabled={readOnly}
                    error={!!item.typeGuessed}
                    options={CONTAINER_CARGO_TYPES.map((type) => ({
                      value: type,
                      label: t(`containerOrders:lineCargo.types.${type}`, { defaultValue: type }),
                    }))}
                    onChange={(e) => setRow(row, { type: e.target.value })}
                  />
                </div>
                <Input
                  size="sm"
                  inputMode="decimal"
                  value={item.weightKg ?? ""}
                  disabled={readOnly}
                  error={rowBlocking && item.weightKg == null}
                  placeholder={t("containers.weightKg")}
                  onChange={(e) => setRow(row, { weightKg: numberOrNull(e.target.value) })}
                />
                <Input
                  size="sm"
                  value={item.description ?? ""}
                  disabled={readOnly}
                  placeholder={t("containers.description")}
                  onChange={(e) => setRow(row, { description: e.target.value })}
                />
                {!readOnly && (
                  <button
                    type="button"
                    className="p-1 text-[#8A9590] hover:text-red-600"
                    onClick={() => set("cargoItems", line.cargoItems.filter((_, i) => i !== row))}
                    aria-label={t("containers.removeRow")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              {rowNotes.map((issue, n) => (
                <div
                  key={`${issue.code}-${n}`}
                  className={`mt-0.5 flex items-start gap-1 text-[11px] ${
                    issue.severity === "blocking" ? "text-[#E24B4A]" : "text-amber-700"
                  }`}
                >
                  {issue.severity === "blocking" ? (
                    <AlertOctagon className="h-3 w-3 mt-[1px] shrink-0" />
                  ) : (
                    <AlertTriangle className="h-3 w-3 mt-[1px] shrink-0" />
                  )}
                  <span>{issue.message}</span>
                </div>
              ))}
            </div>
          );
        })}
        {!readOnly && (
          <button
            type="button"
            onClick={() => set("cargoItems", [...line.cargoItems, { ...EMPTY_ROW }])}
            className="inline-flex items-center gap-1 text-[12px] text-[#C97A3C] hover:underline"
          >
            <Plus className="h-3.5 w-3.5" />
            {t("containers.addRow")}
          </button>
        )}
      </div>

      {/* What the driver reads at each stop */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(["terminalPickup", "loadingSite", "terminalDropoff"] as const).map((role) => (
          <IntakeField
            key={role}
            path={`${base}.stopComments.${role}`}
            label={t(`containers.note.${role}`)}
            issues={issues}
          >
            {() => (
              <IntakeNote
                value={line.stopComments?.[role] ?? ""}
                disabled={readOnly}
                onChange={(e) => set(`stopComments.${role}`, e.target.value)}
              />
            )}
          </IntakeField>
        ))}
      </div>

      <IntakeField path={`${base}.notes`} label={t("containers.notes")} issues={issues}>
        {() => (
          <IntakeNote
            value={line.notes ?? ""}
            disabled={readOnly}
            onChange={(e) => set("notes", e.target.value)}
          />
        )}
      </IntakeField>
      {addingTruck &&
        createPortal(
          <Modal
            isOpen
            onClose={() => setAddingTruck(false)}
            title={t("containers.addTruckTitle")}
            size="lg"
          >
            <div className="flex-1 overflow-y-auto">
              <VehicleForm
                onSubmit={handleCreateTruck}
                loading={savingTruck}
                initialValues={{
                  registrationNumber: (line.truckNr || "").toUpperCase(),
                  // The OPT reference is the name dispatch calls the truck by,
                  // which is exactly what a display name is for.
                  displayName: (line.opt || "").toUpperCase(),
                  type: "TRUCK",
                }}
              />
            </div>
          </Modal>,
          document.body,
        )}
    </div>
  );
};

export default IntakeContainerCard;
